import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PenTool, Lock, CheckCircle2, AlertTriangle, Clock, MessageSquarePlus } from "lucide-react";
import api from "../../api";

// Signing, review and addenda for a consultation note (backend
// clinical/notes/note_signing_routes.py). Shown on the consultation page; tells the
// page when the note is locked so it can stop offering Save.
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// summaryFromTab: a mental-health note already has its patient summary in the
// "Shared with Patient" tab, so signing doesn't ask for a second one.
export default function NoteSigningPanel({ consultationId, onLockedChange, refreshKey, summaryFromTab = false }) {
  const [s, setS] = useState(null);
  const [mode, setMode] = useState(null); // "sign" | "review" | "addendum"
  const [summary, setSummary] = useState("");
  const [comments, setComments] = useState("");
  const [addendum, setAddendum] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.getNoteSigning(consultationId).then((r) => {
    setS(r.data);
    setSummary(r.data.patient_summary || "");
    if (onLockedChange) onLockedChange(Boolean(r.data.locked));
  }).catch(() => setS(null));
  useEffect(() => { if (consultationId) load(); }, [consultationId, refreshKey]); // eslint-disable-line

  if (!s) return null;
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); setMode(null); setComments(""); setAddendum(""); load(); }
    catch (e) { toast.error(e?.response?.data?.error || "Something went wrong."); }
    finally { setBusy(false); }
  };

  const status = {
    draft: { icon: PenTool, cls: "bg-secondary-50 border-secondary-200 text-secondary-700", text: "Not signed yet" },
    signed: { icon: Lock, cls: "bg-emerald-50 border-emerald-200 text-emerald-800", text: `Signed by ${s.signed_by_name || "the clinician"} · ${fmt(s.signed_at)}` },
    in_review: { icon: Clock, cls: "bg-amber-50 border-amber-200 text-amber-900", text: `Signed ${fmt(s.signed_at)} · waiting for supervisor review` },
    changes_requested: { icon: AlertTriangle, cls: "bg-rose-50 border-rose-200 text-rose-800", text: "Supervisor asked for changes - edit the note and sign again" },
    final: { icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-200 text-emerald-800", text: `Signed by ${s.signed_by_name || "the clinician"} and approved` },
  }[s.note_status] || { icon: PenTool, cls: "bg-secondary-50 border-secondary-200", text: s.note_status };
  const Icon = status.icon;
  const lastReview = s.reviews[s.reviews.length - 1];

  return (
    <div className={`rounded-xl border p-3 space-y-2 ${status.cls}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Icon className="w-4 h-4" />
        <span className="font-semibold">{status.text}</span>
        <span className="ml-auto flex flex-wrap gap-2">
          {s.can_sign && !mode && <button type="button" onClick={() => setMode("sign")} className="rounded-lg bg-primary-600 text-white text-xs font-bold px-3 py-1.5">Sign note</button>}
          {s.can_review && !mode && <button type="button" onClick={() => setMode("review")} className="rounded-lg bg-amber-600 text-white text-xs font-bold px-3 py-1.5">Review note</button>}
          {s.locked && !mode && <button type="button" onClick={() => setMode("addendum")} className="inline-flex items-center gap-1 rounded-lg border border-current bg-white text-xs font-bold px-3 py-1.5"><MessageSquarePlus className="w-3.5 h-3.5" /> Add addendum</button>}
        </span>
      </div>
      {s.sign_blocker && !s.can_sign && ["draft", "changes_requested", null, undefined].includes(s.note_status) && (
        <p className="text-xs opacity-80">{s.sign_blocker}</p>
      )}
      {s.note_status === "changes_requested" && lastReview?.comments && (
        <p className="text-sm"><span className="font-semibold">{lastReview.reviewer_name || "Supervisor"}:</span> {lastReview.comments}</p>
      )}
      {s.locked && <p className="text-xs opacity-80">This note is locked. Changes go in as addenda, which keep the original intact.</p>}

      {mode === "sign" && (
        <div className="rounded-lg bg-white border border-secondary-200 p-3 space-y-2 text-secondary-800">
          {summaryFromTab ? (
            <p className="text-[11px] text-secondary-500">The patient's summary is what you wrote in the Shared with Patient tab. Your clinical note stays private.</p>
          ) : (
            <>
              <label htmlFor="patient_summary" className="block text-xs font-semibold">Summary for the patient (optional)</label>
              <p className="text-[11px] text-secondary-500">A few plain sentences about the session. It appears in the patient's app {s.review_required ? "once the note is approved" : "when you sign"}. Your clinical note stays private.</p>
              <textarea id="patient_summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={3}
                className="w-full rounded-md border border-secondary-300 p-2 text-sm" placeholder="e.g. Today we talked about your sleep and practised a breathing exercise. Try it each night before bed." />
            </>
          )}
          <p className="text-xs text-secondary-600">Signing locks the note. {s.review_required ? "It then goes to your supervisor to approve." : ""}</p>
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={() => run(() => api.signNote(consultationId, { patient_summary: summary }), "Note signed")} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">Sign and lock</button>
            <button type="button" onClick={() => setMode(null)} className="rounded-lg border border-secondary-300 text-sm px-4 py-2">Cancel</button>
          </div>
        </div>
      )}

      {mode === "review" && (
        <div className="rounded-lg bg-white border border-secondary-200 p-3 space-y-2 text-secondary-800">
          {s.patient_summary && <p className="text-xs"><span className="font-semibold">Summary for the patient:</span> {s.patient_summary}</p>}
          <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} aria-label="Review comments"
            className="w-full rounded-md border border-secondary-300 p-2 text-sm" placeholder="Comments (required if you ask for changes)" />
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => run(() => api.reviewNote(consultationId, { decision: "approved", comments }), "Note approved")} className="rounded-lg bg-emerald-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">Approve</button>
            <button type="button" disabled={busy || !comments.trim()} onClick={() => run(() => api.reviewNote(consultationId, { decision: "changes_requested", comments }), "Changes requested")} className="rounded-lg border border-rose-300 text-rose-700 text-sm font-semibold px-4 py-2 disabled:opacity-50">Ask for changes</button>
            <button type="button" onClick={() => setMode(null)} className="rounded-lg border border-secondary-300 text-sm px-4 py-2">Cancel</button>
          </div>
        </div>
      )}

      {mode === "addendum" && (
        <div className="rounded-lg bg-white border border-secondary-200 p-3 space-y-2 text-secondary-800">
          <textarea value={addendum} onChange={(e) => setAddendum(e.target.value)} rows={3} aria-label="Addendum"
            className="w-full rounded-md border border-secondary-300 p-2 text-sm" placeholder="What you're adding or correcting, and why" />
          <div className="flex gap-2">
            <button type="button" disabled={busy || !addendum.trim()} onClick={() => run(() => api.addNoteAddendum(consultationId, addendum), "Addendum added")} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">Add addendum</button>
            <button type="button" onClick={() => setMode(null)} className="rounded-lg border border-secondary-300 text-sm px-4 py-2">Cancel</button>
          </div>
        </div>
      )}

      {s.addenda.length > 0 && (
        <ul className="space-y-1.5">
          {s.addenda.map((a) => (
            <li key={a.id} className="rounded-lg bg-white/80 border border-secondary-200 px-3 py-2 text-sm text-secondary-800">
              <p className="text-[11px] font-semibold text-secondary-500">Addendum · {a.author_name || "staff"} · {fmt(a.created_at)}</p>
              <p className="whitespace-pre-line">{a.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
